import React, { useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import AddImage from "../../Global Functions/addImage";
import { addNewCategory } from "../../Redux/Actions/categoryActions";
import toast from "react-hot-toast";
import "./addSubCategories.css";

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const UploadField = ({ label, fieldKey, value, onUpload, onRemove, loading, required }) => (
  <div className="ab-field">
    <label className="ab-label">
      {label}
      {required ? <span className="ab-req"> *</span> : <span className="ab-optional"> (Optional)</span>}
    </label>
    {value ? (
      <div className="aa-image-preview">
        <img src={value} alt={label} className="aa-preview-img" />
        <div className="aa-preview-info">
          <span className="aa-preview-label">Uploaded</span>
          <button type="button" className="aa-remove-btn" onClick={() => onRemove(fieldKey)} disabled={loading}>
            <TrashIcon />
            Remove
          </button>
        </div>
      </div>
    ) : (
      <label className="aa-upload-zone" htmlFor={`upload-${fieldKey}`}>
        <input
          id={`upload-${fieldKey}`}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onUpload(e, fieldKey)}
          disabled={loading}
        />
        <div className="aa-upload-icon"><UploadIcon /></div>
        <span className="aa-upload-text">Click to upload</span>
        <span className="aa-upload-hint">PNG, JPG, SVG up to 2MB</span>
      </label>
    )}
  </div>
);

const AddSubSubCategories = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const [subCategory, setSubCategory] = useState({
    name: "",
    position: Math.floor(Math.random() * 1000) + 1,
    image: "",
    icon: "",
    three_d_icon: "",
    parent: id,
    business_type: [],
  });

  const handleImageUpload = async (event, type) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    const responseUrl = await AddImage(uploadedFile);
    if (responseUrl) {
      setSubCategory((prev) => ({ ...prev, [type]: responseUrl }));
    }
  };

  const handleRemove = (fieldKey) => {
    setSubCategory((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleNextSection = () => {
    if (activeSection === 0 && !subCategory.name.trim()) {
      toast.error("Sub-category name is required");
      return;
    }

    if (activeSection === 0 && subCategory.business_type.length === 0) {
      toast.error("Please select at least one type");
      return;
    }
    setActiveSection((s) => Math.min(1, s + 1));
  };

  const handleReset = () => {
    setSubCategory({
      name: "",
      position: Math.floor(Math.random() * 1000) + 1,
      image: "",
      icon: "",
      three_d_icon: "",
      parent: id,
      business_type: [],
    });
    setActiveSection(0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subCategory.name.trim()) return toast.error("Sub-category name is required");
    if (subCategory.business_type.length === 0) return toast.error("Please select at least one type");

    setLoading(true);
    try {
      const filteredData = Object.fromEntries(
        Object.entries(subCategory).filter(([_, value]) => value !== "" && value !== null && value !== undefined)
      );
      const response = await dispatch(addNewCategory(filteredData));
      if (response?.statusCode === 200) {
        toast.success("Sub-category added successfully!");
        handleReset();
        navigate(`/categories/view/${id}`);
      } else {
        toast.error(response?.message || "Failed to add sub-category");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const sections = ["Basic Info", "Media"];

  return (
    <MainLayout>
      <div className="ab-wrapper">
        <div className="ab-header">
          <div className="ab-header-left">
            <div className="ab-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                <line x1="12" y1="11" x2="12" y2="17" />
                <line x1="9" y1="14" x2="15" y2="14" />
              </svg>
            </div>
            <div>
              <h1 className="ab-title">Add Sub-Category</h1>
              <p className="ab-subtitle">Fill in the details to add a new sub-category</p>
            </div>
          </div>
          <Link to={`/categories/view/${id}`} className="ab-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            View Sub-Category List
          </Link>
        </div>

        <div className="ab-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`ab-nav-pill ${activeSection === i ? "ab-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading}
            >
              <span className="ab-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="ab-form" >
          <div className={`ab-section ${activeSection === 0 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">01</span>
              <h2 className="ab-section-title">Basic Information</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <div className="ab-field">
                <label className="ab-label">Sub-Category Name <span className="ab-req">*</span></label>
                <input
                  className="ab-input"
                  type="text"
                  value={subCategory.name}
                  onChange={(e) => setSubCategory((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Apartment"
                  disabled={loading}
                />
              </div>

              <div className="ab-field">
                <label className="ab-label">Type <span className="ab-req">*</span></label>
                <div className="ab-multi-select">
                  {["fresh", "resale", "business", "rental"].map((option) => (
                    <label key={option} className="ab-checkbox-label">
                      <input
                        type="checkbox"
                        value={option}
                        checked={subCategory.business_type.includes(option)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSubCategory((prev) => ({
                            ...prev,
                            business_type: checked
                              ? [...prev.business_type, option]
                              : prev.business_type.filter((t) => t !== option),
                          }));
                        }}
                        disabled={loading}
                      />
                      <span className="ab-checkbox-text">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>


          <div className={`ab-section ${activeSection === 1 ? "ab-section--visible" : ""}`}>
            <div className="ab-section-header">
              <span className="ab-section-badge">02</span>
              <h2 className="ab-section-title">Media</h2>
            </div>
            <div className="ab-grid ab-grid-2">
              <UploadField
                label="Sub-Category Image"
                fieldKey="image"
                value={subCategory.image}
                onUpload={handleImageUpload}
                onRemove={handleRemove}
                loading={loading}
                required={true}
              />
              <UploadField
                label="Sub-Category Icon"
                fieldKey="icon"
                value={subCategory.icon}
                onUpload={handleImageUpload}
                onRemove={handleRemove}
                loading={loading}
                required={true}
              />
              <UploadField
                label="3D Icon"
                fieldKey="three_d_icon"
                value={subCategory.three_d_icon}
                onUpload={handleImageUpload}
                onRemove={handleRemove}
                loading={loading}
                required={true}
              />
            </div>
          </div>

          <div className="ab-footer">
            <div className="ab-footer-nav">
              <button
                type="button"
                className="ab-btn ab-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button type="button" className="ab-btn ab-btn--primary" onClick={handleNextSection} disabled={loading}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="ab-btn ab-btn--submit" disabled={loading}>
                  {loading ? (
                    "Saving..."
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Save Sub-Category
                    </>
                  )}
                </button>
              )}
            </div>
            <Link to={`/categories/view/${id}`} className="ab-btn ab-btn--cancel" onClick={handleReset}>
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddSubSubCategories;